import { AttachQuotePayload } from '@/services/quotes/types';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { toaster } from '@/shared/ui/toaster.component';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { formatCurrencyFromString } from '@/shared/utils';
import {
  Button,
  Field,
  RadioCard,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { useQueryClient } from '@tanstack/react-query';
import { formOptions } from '@tanstack/react-form';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Home,
  Layers,
  SplitSquareHorizontal,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import { useConstructionTypes } from '../../hooks/homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../../hooks/homeowner/use-insured-objects.hook';
import { usePropertyClassifications } from '../../hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../../hooks/homeowner/use-property-types.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useUpdateQuote } from '../../hooks/homeowner/use-update-quote.hook';
import { PropertyDeniedDialog } from './property-denied.dialog';
// TODO: productElementId será dinâmico via InsureMO no futuro

type InsuredObjectRow = {
  insuredObjectType: string;
  riskValue: string;
};

const PREDIO_CONTEUDO_VALUE_SEPARATOR = '|';

const formOpts = formOptions({
  defaultValues: {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    propertyType: '',
    isLandmarked: false,
    constructionType: '',
    propertyClassification: '',
    insuredObjectType: '',
  },
});

export const StepPropertyData = withForm({
  ...formOpts,
  props: {
    onNext: () => {},
    onBack: () => {},
    stepCode: '',
    prefilledInsuredObjects: undefined as InsuredObjectRow[] | undefined,
    onInsuredObjectsSave: undefined as
      | ((_objects: InsuredObjectRow[]) => void)
      | undefined,
    isViewMode: false,
  },
  render: ({
    form,
    onNext,
    onBack,
    stepCode,
    prefilledInsuredObjects,
    onInsuredObjectsSave,
    isViewMode,
  }) => {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, isError, error } = useUpdateQuote();
    const { mutateAsync: fetchZipcode, isPending: isLoadingCep } =
      useFetchZipcode();
    const zipcodeLookupCacheRef = useRef<Record<string, boolean>>({});
    const [hasResolvedCep, setHasResolvedCep] = useState(false);
    const [isGenericCep, setIsGenericCep] = useState(false);
    const [cepDenied, setCepDenied] = useState<{
      open: boolean;
      detail: string;
      errors: string[];
    }>({ open: false, detail: '', errors: [] });
    // `isGenericCep` (CEP terminado em `000`) é só um PALPITE de que o CEP não
    // tem logradouro — e um palpite errado: a Praça da Sé é `01001-000` e tem
    // rua. O que decide de verdade é a resposta ter vindo sem rua; nesse caso o
    // corretor precisa poder digitar, senão o campo fica vazio, travado e
    // obrigatório ao mesmo tempo.
    const resolvedStreet = String(form.state.values.street || '').trim();
    const canEditStreetAndNeighborhood =
      hasResolvedCep && (isGenericCep || !resolvedStreet);
    const { data: propertyTypes, isPending: isLoadingPropertyTypes } =
      usePropertyTypes();
    const {
      data: propertyClassifications,
      isPending: isLoadingPropertyClassifications,
      isError: hasPropertyClassificationsError,
      error: propertyClassificationsError,
      refetch: refetchPropertyClassifications,
    } = usePropertyClassifications();
    const {
      data: insuredObjectTypes,
      isPending: isLoadingInsuredObjectTypes,
      isError: hasInsuredObjectTypesError,
      error: insuredObjectTypesError,
      refetch: refetchInsuredObjectTypes,
    } = useInsuredObjects();
    const {
      data: constructionTypes,
      isPending: isLoadingConstructionTypes,
      isError: hasConstructionTypesError,
      error: constructionTypesError,
      refetch: refetchConstructionTypes,
    } = useConstructionTypes();
    const { updateProperty } = useQuoteSummary();

    useEffect(() => {
      const cleanCep = String(form.state.values.cep || '').replace(/\D/g, '');
      if (cleanCep.length !== 8) {
        if (hasResolvedCep) setHasResolvedCep(false);
        if (isGenericCep) setIsGenericCep(false);
        return;
      }

      if (!hasResolvedCep) setHasResolvedCep(true);
      const nextIsGeneric = cleanCep.endsWith('000');
      if (nextIsGeneric !== isGenericCep) setIsGenericCep(nextIsGeneric);
    }, [form.state.values.cep, hasResolvedCep, isGenericCep]);

    const normalize = (value: string) =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    const insuredObjectOptions = useMemo(() => {
      const base =
        (insuredObjectTypes ?? []).map(item => ({
          label: item.description,
          value: String(item.code ?? ''),
        })) ?? [];

      const hasCombined = base.some(
        o => normalize(o.label) === 'predio e conteudo'
      );
      if (hasCombined) return base;

      const predio = base.find(o => normalize(o.label) === 'predio');
      const conteudo = base.find(o => normalize(o.label) === 'conteudo');
      if (!predio || !conteudo) return base;

      return [
        ...base,
        {
          label: 'Prédio e Conteúdo',
          value: `${predio.value}${PREDIO_CONTEUDO_VALUE_SEPARATOR}${conteudo.value}`,
        },
      ];
    }, [insuredObjectTypes]);

    const [selectedInsuredObjectType, setSelectedInsuredObjectType] =
      useState('');
    const [insuredObjects, setInsuredObjects] = useState<InsuredObjectRow[]>(
      []
    );
    const hasHydratedPrefilledInsuredObjectsRef = useRef(false);
    const [insuredObjectsErrors, setInsuredObjectsErrors] = useState<
      Record<string, string>
    >({});
    const [showInsuredObjectError, setShowInsuredObjectError] = useState(false);
    const [showPropertyTypeFooterError, setShowPropertyTypeFooterError] =
      useState(false);

    const constructionTypesErrorShownRef = useRef(false);
    const propertyClassificationsErrorShownRef = useRef(false);
    const insuredObjectTypesErrorShownRef = useRef(false);

    const persistedInsuredObjectType = String(
      form.state.values.insuredObjectType ?? ''
    ).trim();

    const predioOption = insuredObjectOptions.find(
      o => normalize(o.label) === 'predio'
    );
    const conteudoOption = insuredObjectOptions.find(
      o => normalize(o.label) === 'conteudo'
    );
    const combinedOption = insuredObjectOptions.find(
      o => normalize(o.label) === 'predio e conteudo'
    );

    const insuredObjectCardOptions = [
      predioOption && {
        ...predioOption,
        displayLabel: 'Predio',
        icon: Building2,
      },
      conteudoOption && {
        ...conteudoOption,
        displayLabel: 'Conteúdo',
        icon: Layers,
      },
      combinedOption && {
        ...combinedOption,
        displayLabel: 'Prédio e Conteudo',
        icon: SplitSquareHorizontal,
      },
    ].filter(Boolean) as Array<{
      label: string;
      value: string;
      displayLabel: string;
      icon: typeof Building2;
    }>;

    const orderInsuredObjects = (rows: InsuredObjectRow[]) => {
      const order = insuredObjectCardOptions.map(option => option.value);
      return rows
        .filter(row => String(row.insuredObjectType || '').trim())
        .sort((a, b) => {
          const indexA = order.indexOf(a.insuredObjectType);
          const indexB = order.indexOf(b.insuredObjectType);
          return (
            (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
            (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
          );
        });
    };

    const isIndividualInsuredObject = (insuredObjectType: string) =>
      insuredObjectType === predioOption?.value ||
      insuredObjectType === conteudoOption?.value;

    const isCombinedInsuredObject = (insuredObjectType: string) =>
      insuredObjectType === combinedOption?.value;

    const selectedRiskValue =
      insuredObjects.find(
        row => row.insuredObjectType === selectedInsuredObjectType
      )?.riskValue || '';

    useEffect(() => {
      if (!persistedInsuredObjectType) return;
      setSelectedInsuredObjectType(
        current => current || persistedInsuredObjectType
      );
    }, [persistedInsuredObjectType]);

    useEffect(() => {
      if (hasHydratedPrefilledInsuredObjectsRef.current) return;
      if (!prefilledInsuredObjects || prefilledInsuredObjects.length === 0) {
        return;
      }

      const sanitizedPrefill = prefilledInsuredObjects
        .map(item => ({
          insuredObjectType: String(item.insuredObjectType ?? '').trim(),
          riskValue: String(item.riskValue ?? '').trim(),
        }))
        .filter(item => item.insuredObjectType);

      if (sanitizedPrefill.length === 0) return;

      const combinedPrefill = sanitizedPrefill.filter(item =>
        isCombinedInsuredObject(item.insuredObjectType)
      );
      const individualPrefill = sanitizedPrefill.filter(item =>
        isIndividualInsuredObject(item.insuredObjectType)
      );

      const nextInsuredObjects =
        combinedPrefill.length > 0
          ? combinedPrefill.slice(0, 1)
          : orderInsuredObjects(individualPrefill).slice(0, 2);

      if (nextInsuredObjects.length === 0) return;

      hasHydratedPrefilledInsuredObjectsRef.current = true;
      setInsuredObjects(nextInsuredObjects);
      setSelectedInsuredObjectType(nextInsuredObjects[0].insuredObjectType);
      form.setFieldValue(
        'insuredObjectType',
        nextInsuredObjects[0].insuredObjectType
      );
    }, [form, prefilledInsuredObjects, insuredObjectOptions]);

    useEffect(() => {
      if (
        hasConstructionTypesError &&
        !constructionTypesErrorShownRef.current
      ) {
        constructionTypesErrorShownRef.current = true;
        toaster.create({
          type: 'error',
          title: 'Erro ao carregar tipos de construção',
          description:
            constructionTypesError?.message ??
            'Não foi possível carregar os tipos de construção. Tente novamente.',
        });
      }
      if (!hasConstructionTypesError && !isLoadingConstructionTypes) {
        constructionTypesErrorShownRef.current = false;
      }
    }, [
      hasConstructionTypesError,
      constructionTypesError,
      isLoadingConstructionTypes,
    ]);

    useEffect(() => {
      if (
        hasPropertyClassificationsError &&
        !propertyClassificationsErrorShownRef.current
      ) {
        propertyClassificationsErrorShownRef.current = true;
        toaster.create({
          type: 'error',
          title: 'Erro ao carregar classificações do imóvel',
          description:
            propertyClassificationsError?.message ??
            'Não foi possível carregar as classificações do imóvel. Tente novamente.',
        });
      }
      if (
        !hasPropertyClassificationsError &&
        !isLoadingPropertyClassifications
      ) {
        propertyClassificationsErrorShownRef.current = false;
      }
    }, [
      hasPropertyClassificationsError,
      propertyClassificationsError,
      isLoadingPropertyClassifications,
    ]);

    useEffect(() => {
      if (
        hasInsuredObjectTypesError &&
        !insuredObjectTypesErrorShownRef.current
      ) {
        insuredObjectTypesErrorShownRef.current = true;
        toaster.create({
          type: 'error',
          title: 'Erro ao carregar objetos segurados',
          description:
            insuredObjectTypesError?.message ??
            'Não foi possível carregar os objetos segurados. Tente novamente.',
        });
      }
      if (!hasInsuredObjectTypesError && !isLoadingInsuredObjectTypes) {
        insuredObjectTypesErrorShownRef.current = false;
      }
    }, [
      hasInsuredObjectTypesError,
      insuredObjectTypesError,
      isLoadingInsuredObjectTypes,
    ]);

    const validateInsuredObjects = () => {
      if (!selectedInsuredObjectType) {
        setShowInsuredObjectError(true);
        return false;
      }

      const hasAtLeastOneDeclaredRisk = insuredObjects.some(row =>
        String(row.riskValue || '').trim()
      );

      if (!hasAtLeastOneDeclaredRisk) {
        setInsuredObjectsErrors({
          [selectedInsuredObjectType]: 'Risco declarado é obrigatório',
        });
        return false;
      }

      setInsuredObjectsErrors({});
      return true;
    };

    try {
      const { id } = useQuoteParams();

      const handleSubmit = async () => {
        if (!id) {
          return;
        }

        const cleanCep = (form.state.values.cep || '').replace(/\D/g, '');

        if (
          cleanCep.length === 8 &&
          zipcodeLookupCacheRef.current[cleanCep] !== true
        ) {
          try {
            const result = await fetchZipcode({ zipcode: cleanCep, policyId: id });
            if (
              result.success &&
              result.data &&
              'blocked' in result.data &&
              result.data.blocked
            ) {
              if (id) {
                queryClient.invalidateQueries({
                  queryKey: ['proposal-details', id],
                });
              }
              queryClient.invalidateQueries({ queryKey: ['quotes'] });
              setCepDenied({
                open: true,
                detail: 'Risco não aceito',
                errors: ['CEP bloqueado para cotação'],
              });
              return;
            }

            if (result.success && result.data) {
              zipcodeLookupCacheRef.current[cleanCep] = true;
              setHasResolvedCep(true);
              const isGeneric = cleanCep.endsWith('000');
              setIsGenericCep(isGeneric);
              form.setFieldValue('street', result.data.street || '');
              form.setFieldValue(
                'neighborhood',
                result.data.neighborhood || ''
              );
              form.setFieldValue('city', result.data.city || '');
              form.setFieldValue('state', result.data.state || '');
            } else {
              zipcodeLookupCacheRef.current[cleanCep] = false;
              setHasResolvedCep(false);
              setIsGenericCep(false);
              form.setFieldValue('street', '');
              form.setFieldValue('neighborhood', '');
              form.setFieldValue('city', '');
              form.setFieldValue('state', '');
              toaster.create({
                type: 'error',
                title: 'CEP não encontrado',
                description: 'Verifique o CEP informado e tente novamente.',
              });
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error && error.message.trim()
                ? error.message
                : 'Verifique o CEP informado e tente novamente.';
            zipcodeLookupCacheRef.current[cleanCep] = false;
            setHasResolvedCep(false);
            setIsGenericCep(false);
            form.setFieldValue('street', '');
            form.setFieldValue('neighborhood', '');
            form.setFieldValue('city', '');
            form.setFieldValue('state', '');
            toaster.create({
              type: 'error',
              title: 'Erro ao buscar CEP',
              description: errorMessage,
            });
          }

          await form.validateField(
            'cep' as keyof typeof form.state.values,
            'change'
          );
        }

        const fieldsToValidate = [
          'cep',
          'constructionType',
          'propertyClassification',
          'propertyType',
          'street',
          'number',
          'neighborhood',
          'city',
          'state',
        ];
        const validationPromises = fieldsToValidate.map(fieldName =>
          form.validateField(
            fieldName as keyof typeof form.state.values,
            'change'
          )
        );
        const results = await Promise.all(validationPromises);
        const hasErrors = results.some(result => result.length > 0);

        const hasValidInsuredObjects = validateInsuredObjects();
        const isPropertyTypeMissing = !String(
          form.state.values.propertyType || ''
        ).trim();
        setShowPropertyTypeFooterError(isPropertyTypeMissing);

        if (!hasErrors && hasValidInsuredObjects) {
          try {
            const primaryInsuredObjectType =
              insuredObjects[0]?.insuredObjectType || '1';
            form.setFieldValue('insuredObjectType', primaryInsuredObjectType);

            // Converte string formatada de moeda para número
            const parseCurrencyToNumber = (currencyString: string): number => {
              if (!currencyString || !currencyString.trim()) return 0;
              // Remove "R$", espaços, pontos (separadores de milhar) e converte vírgula para ponto
              const cleaned = currencyString
                .replace(/R\$\s*/g, '')
                .replace(/\./g, '')
                .replace(',', '.')
                .trim();
              return parseFloat(cleaned) || 0;
            };

            // Cria o scheduleExt a partir dos insuredObjects
            const scheduleExt = insuredObjects
              .filter(
                row => row.insuredObjectType && row.insuredObjectType.trim()
              )
              .map(row => {
                const insuredObject = row.insuredObjectType.trim();
                const value = parseCurrencyToNumber(row.riskValue);
                return {
                  insuredObject,
                  value,
                };
              });

            const payload: Partial<AttachQuotePayload> = {
              step: stepCode || '',
              insuredObject: {
                address: form.state.values.street || '',
                addressComplement: form.state.values.complement || '',
                addressNumber: form.state.values.number || '',
                addressType: 1,
                city: form.state.values.city || '',
                state: form.state.values.state || '',
                constructionType: form.state.values.constructionType,
                countryCode: 'BR',
                latitude: '',
                longitude: '',
                listedPropertyFlag: form.state.values.isLandmarked ? 'S' : 'N',
                neighborhood: form.state.values.neighborhood || '',
                propertyClassification:
                  form.state.values.propertyClassification || '',
                street: form.state.values.street || '',
                postCode: cleanCep,
                propertyType: form.state.values.propertyType,
                scheduleExt: scheduleExt.length > 0 ? scheduleExt : undefined,
              },
            };

            const result = await mutateAsync({
              id: id || '',
              payload,
            });

            if (result.success) {
              const propertyTypeCode = form.state.values.propertyType;
              const propertyTypeValue =
                propertyTypeCode === '1' ||
                propertyTypes
                  ?.find(
                    pt => String(pt.code ?? '') === String(propertyTypeCode)
                  )
                  ?.name.toLowerCase()
                  .includes('casa')
                  ? ('house' as const)
                  : ('apartment' as const);

              const constructionTypeLabel = constructionTypes?.find(
                type =>
                  String(type.code ?? '') ===
                  String(form.state.values.constructionType ?? '')
              )?.description;
              const propertyClassificationLabel = propertyClassifications?.find(
                item =>
                  String(item.code ?? '') ===
                  String(form.state.values.propertyClassification ?? '')
              )?.description;
              const insuredObjectTypeLabel =
                insuredObjectOptions.find(
                  o => o.value === primaryInsuredObjectType
                )?.label ||
                insuredObjectTypes?.find(
                  item => String(item.code ?? '') === primaryInsuredObjectType
                )?.description;

              updateProperty({
                propertyType: propertyTypeValue,
                city: form.state.values.city,
                state: form.state.values.state,
                cep: form.state.values.cep,
                address: form.state.values.street,
                number: form.state.values.number,
                complement: form.state.values.complement,
                neighborhood: form.state.values.neighborhood,
                constructionType: form.state.values.constructionType,
                propertyClassification:
                  form.state.values.propertyClassification,
                insuredObjectType: primaryInsuredObjectType,
                constructionTypeLabel,
                propertyClassificationLabel,
                insuredObjectTypeLabel,
              });
              onInsuredObjectsSave?.(insuredObjects);
              onNext?.();
            }
          } catch (error) {
            if (error instanceof ValidationError) {
              const isRejection = !!error.detail;
              if (!isRejection) {
                toaster.create({
                  type: 'error',
                  title: 'Erro de validação',
                  description:
                    error.messages.length > 0
                      ? error.messages.join('\n')
                      : error.message !== 'Erro de validação'
                        ? error.message
                        : 'Erros encontrados na validação.',
                });
              }
            }
          }
        }
      };

      return (
        <>
          <div className='space-y-6 sm:space-y-8'>
            <div className='space-y-4'>
              <h2 className='text-lg font-bold text-alba-black-500'>
                Dados do Imóvel
              </h2>

              <div className='space-y-4'>
                <div className='w-full sm:w-1/2'>
                  <form.AppField
                    name='cep'
                    validators={{
                      onChange: ({ value }) => {
                        const stringValue = String(value || '');
                        if (!stringValue || stringValue.trim() === '') {
                          return 'CEP é obrigatório';
                        }
                        const cleanCep = stringValue.replace(/\D/g, '');
                        if (cleanCep.length !== 8) {
                          return 'CEP inválido ou não encontrado';
                        }

                        // Se já fizemos lookup desse CEP e ele falhou, exibe o erro no campo
                        if (zipcodeLookupCacheRef.current[cleanCep] === false) {
                          return 'CEP inválido ou não encontrado';
                        }

                        return undefined;
                      },
                    }}
                    listeners={{
                      onBlur: async ({ value }) => {
                        const stringValue = String(value || '');
                        if (!stringValue || stringValue.trim() === '') return;

                        const cleanCep = stringValue.replace(/\D/g, '');
                        if (cleanCep.length !== 8) return;

                        try {
                          const result = await fetchZipcode({ zipcode: cleanCep, policyId: id });
                          if (
                            result.success &&
                            result.data &&
                            'blocked' in result.data &&
                            result.data.blocked
                          ) {
                            if (id) {
                              queryClient.invalidateQueries({
                                queryKey: ['proposal-details', id],
                              });
                            }
                            queryClient.invalidateQueries({
                              queryKey: ['quotes'],
                            });
                            setCepDenied({
                              open: true,
                              detail: 'Risco não aceito',
                              errors: ['CEP bloqueado para cotação'],
                            });
                            return;
                          }

                          if (result.success && result.data) {
                            zipcodeLookupCacheRef.current[cleanCep] = true;
                            setHasResolvedCep(true);
                            const isGeneric = cleanCep.endsWith('000');
                            setIsGenericCep(isGeneric);
                            form.setFieldValue(
                              'street',
                              result.data.street || ''
                            );
                            form.setFieldValue(
                              'neighborhood',
                              result.data.neighborhood || ''
                            );
                            form.setFieldValue('city', result.data.city || '');
                            form.setFieldValue(
                              'state',
                              result.data.state || ''
                            );
                          } else {
                            zipcodeLookupCacheRef.current[cleanCep] = false;
                            setHasResolvedCep(false);
                            setIsGenericCep(false);
                            form.setFieldValue('street', '');
                            form.setFieldValue('neighborhood', '');
                            form.setFieldValue('city', '');
                            form.setFieldValue('state', '');
                            toaster.create({
                              type: 'error',
                              title: 'CEP não encontrado',
                              description:
                                'Verifique o CEP informado e tente novamente.',
                            });
                          }
                        } catch (error) {
                          const errorMessage =
                            error instanceof Error && error.message.trim()
                              ? error.message
                              : 'Verifique o CEP informado e tente novamente.';
                          zipcodeLookupCacheRef.current[cleanCep] = false;
                          setHasResolvedCep(false);
                          setIsGenericCep(false);
                          form.setFieldValue('street', '');
                          form.setFieldValue('neighborhood', '');
                          form.setFieldValue('city', '');
                          form.setFieldValue('state', '');
                          toaster.create({
                            type: 'error',
                            title: 'Erro ao buscar CEP',
                            description: errorMessage,
                          });
                        }

                        await form.validateField(
                          'cep' as keyof typeof form.state.values,
                          'change'
                        );
                      },
                    }}
                    children={field => (
                      <field.TextField
                        label='CEP'
                        placeholder='XXXXX-XXX'
                        required
                        maxLength={FIELD_MAX_LENGTHS.cep}
                        mask='99999-999'
                        disabled={isViewMode || isLoadingCep || isPending}
                      />
                    )}
                  />
                </div>

                {hasResolvedCep && (
                  <>
                    <form.AppField
                      name='street'
                      validators={{
                        onChange: ({ value }) => {
                          const stringValue = String(value || '');
                          if (!stringValue || stringValue.trim() === '') {
                            return 'Endereço é obrigatório';
                          }
                          return undefined;
                        },
                      }}
                      children={field => (
                        <field.TextField
                          label='Endereço'
                          placeholder='Rua, Avenida ...'
                          required
                          maxLength={FIELD_MAX_LENGTHS.street}
                          disabled={
                            isViewMode ||
                            isPending ||
                            !canEditStreetAndNeighborhood
                          }
                        />
                      )}
                    />

                    <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-10'>
                      <form.AppField
                        name='number'
                        validators={{
                          onChange: ({ value }) => {
                            const stringValue = String(value || '');
                            if (!stringValue || stringValue.trim() === '') {
                              return 'Número é obrigatório';
                            }
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.TextField
                            label='Número'
                            placeholder='Número'
                            required
                            maxLength={FIELD_MAX_LENGTHS.addressNumber}
                            disabled={isViewMode || isPending}
                          />
                        )}
                      />

                      <form.AppField
                        name='complement'
                        children={field => (
                          <field.TextField
                            label='Complemento'
                            placeholder='Apto, Bloco ...'
                            maxLength={FIELD_MAX_LENGTHS.complement}
                            disabled={isViewMode || isPending}
                          />
                        )}
                      />
                    </div>

                    <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3 lg:gap-10'>
                      <form.AppField
                        name='neighborhood'
                        validators={{
                          onChange: ({ value }) => {
                            const stringValue = String(value || '');
                            if (!stringValue || stringValue.trim() === '') {
                              return 'Bairro é obrigatório';
                            }
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.TextField
                            label='Bairro'
                            placeholder='Bairro'
                            required
                            maxLength={FIELD_MAX_LENGTHS.neighborhood}
                            disabled={
                              isViewMode ||
                              isPending ||
                              !canEditStreetAndNeighborhood
                            }
                          />
                        )}
                      />

                      <form.AppField
                        name='city'
                        validators={{
                          onChange: ({ value }) => {
                            const stringValue = String(value || '');
                            if (!stringValue || stringValue.trim() === '') {
                              return 'Cidade é obrigatória';
                            }
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.TextField
                            label='Cidade'
                            placeholder='Cidade'
                            required
                            maxLength={FIELD_MAX_LENGTHS.city}
                            disabled={true}
                          />
                        )}
                      />

                      <form.AppField
                        name='state'
                        validators={{
                          onChange: ({ value }) => {
                            const stringValue = String(value || '');
                            if (!stringValue || stringValue.trim() === '') {
                              return 'Estado é obrigatório';
                            }
                            return undefined;
                          },
                        }}
                        children={field => (
                          <field.SelectField
                            label='Estado'
                            placeholder='UF'
                            required
                            disabled={true}
                            options={[
                              { value: 'AC', label: 'Acre' },
                              { value: 'AL', label: 'Alagoas' },
                              { value: 'AP', label: 'Amapá' },
                              { value: 'AM', label: 'Amazonas' },
                              { value: 'BA', label: 'Bahia' },
                              { value: 'CE', label: 'Ceará' },
                              { value: 'DF', label: 'Distrito Federal' },
                              { value: 'ES', label: 'Espírito Santo' },
                              { value: 'GO', label: 'Goiás' },
                              { value: 'MA', label: 'Maranhão' },
                              { value: 'MT', label: 'Mato Grosso' },
                              { value: 'MS', label: 'Mato Grosso do Sul' },
                              { value: 'MG', label: 'Minas Gerais' },
                              { value: 'PA', label: 'Pará' },
                              { value: 'PB', label: 'Paraíba' },
                              { value: 'PR', label: 'Paraná' },
                              { value: 'PE', label: 'Pernambuco' },
                              { value: 'PI', label: 'Piauí' },
                              { value: 'RJ', label: 'Rio de Janeiro' },
                              { value: 'RN', label: 'Rio Grande do Norte' },
                              { value: 'RS', label: 'Rio Grande do Sul' },
                              { value: 'RO', label: 'Rondônia' },
                              { value: 'RR', label: 'Roraima' },
                              { value: 'SC', label: 'Santa Catarina' },
                              { value: 'SP', label: 'São Paulo' },
                              { value: 'SE', label: 'Sergipe' },
                              { value: 'TO', label: 'Tocantins' },
                            ]}
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Dados do Risco */}
            <div className='space-y-4'>
              <h2 className='text-lg font-bold text-alba-black-500'>
                Dados do Risco
              </h2>

              <div className='space-y-4 sm:space-y-6'>
                <RequiredFieldTooltip active>
                  <div className='space-y-2'>
                    <label className='text-sm font-bold text-alba-black-500'>
                      Tipo de Imóvel
                      <span className='text-alba-error-red-500'>*</span>
                    </label>
                  <form.AppField
                    name='propertyType'
                    validators={{
                      onChange: ({ value }) => {
                        const stringValue = String(value || '');
                        if (!stringValue || stringValue.trim() === '') {
                          return 'Tipo de imóvel é obrigatório';
                        }
                        return undefined;
                      },
                    }}
                    children={field => (
                      <RadioCard.Root
                        value={field.state.value}
                        onValueChange={details => {
                          setShowPropertyTypeFooterError(false);
                          field.handleChange(details.value ?? '');
                        }}
                        disabled={
                          isViewMode || isLoadingPropertyTypes || isPending
                        }
                      >
                        <div className='flex gap-2'>
                          {propertyTypes?.map((propertyType, index) => {
                            const propertyTypeCode = String(
                              propertyType.code ?? ''
                            );
                            const isHouse =
                              propertyTypeCode === '1' ||
                              propertyType.name.toLowerCase().includes('casa');
                            const Icon = isHouse ? Home : Building2;

                            return (
                              <RadioCard.Item
                                key={
                                  propertyType.id ||
                                  propertyType.code ||
                                  `property-type-${index}`
                                }
                                value={propertyTypeCode}
                                className='w-44 h-[120px] rounded-xl'
                              >
                                <RadioCard.ItemControl className='flex-col items-start justify-center h-full relative px-4'>
                                  <div className='absolute top-3 right-3'>
                                    <RadioCard.RadioButton />
                                  </div>
                                  <div className='flex flex-col items-start justify-center gap-2'>
                                    <Icon
                                      className='w-8 h-8'
                                      strokeWidth={1.6}
                                    />
                                    <RadioCard.ItemText className='text-left'>
                                      {propertyType.name}
                                    </RadioCard.ItemText>
                                  </div>
                                </RadioCard.ItemControl>
                                <RadioCard.ItemHiddenInput />
                              </RadioCard.Item>
                            );
                          })}
                        </div>
                      </RadioCard.Root>
                    )}
                  />
                  {showPropertyTypeFooterError && (
                    <Field.Root invalid>
                      <Field.ErrorText>
                        Tipo de imóvel é obrigatório
                      </Field.ErrorText>
                    </Field.Root>
                  )}
                  </div>
                </RequiredFieldTooltip>

                <form.AppField
                  name='isLandmarked'
                  children={field => (
                    <field.CheckboxField
                      label='Imóvel Tombado?'
                      disabled={isViewMode}
                    />
                  )}
                />

                <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-10'>
                  <div className='space-y-2'>
                    <form.AppField
                      name='constructionType'
                      validators={{
                        onChange: ({ value }) => {
                          const stringValue = String(value || '');
                          if (!stringValue || stringValue.trim() === '') {
                            return 'Tipo de construção é obrigatório';
                          }
                          return undefined;
                        },
                      }}
                      children={field => (
                        <field.SelectField
                          label='Tipo de Construção'
                          placeholder={
                            hasConstructionTypesError
                              ? 'Erro ao carregar'
                              : 'Selecione'
                          }
                          required
                          disabled={
                            isViewMode ||
                            isLoadingCep ||
                            isPending ||
                            isLoadingConstructionTypes ||
                            hasConstructionTypesError ||
                            (constructionTypes?.length ?? 0) === 0
                          }
                          options={
                            constructionTypes?.map(type => ({
                              value: String(type.code ?? ''),
                              label: type.description,
                            })) ?? []
                          }
                        />
                      )}
                    />

                    {hasConstructionTypesError && (
                      <div className='rounded-lg border border-alba-error-red-200 bg-alba-error-red-50 p-3 text-sm text-alba-error-red-700'>
                        <p className='font-medium'>
                          Não foi possível carregar os tipos de construção.
                        </p>
                        <p className='mt-1 text-xs text-alba-error-red-600'>
                          {constructionTypesError?.message ??
                            'Erro inesperado. Tente novamente.'}
                        </p>
                        <button
                          type='button'
                          onClick={() => refetchConstructionTypes()}
                          className='mt-2 text-xs font-semibold text-alba-purple-500 hover:text-alba-purple-600'
                        >
                          Tentar novamente
                        </button>
                      </div>
                    )}
                  </div>

                  <form.AppField
                    name='propertyClassification'
                    validators={{
                      onChange: ({ value }) => {
                        const stringValue = String(value || '');
                        if (!stringValue || stringValue.trim() === '') {
                          return 'Classificação do imóvel é obrigatória';
                        }
                        return undefined;
                      },
                    }}
                    children={field => (
                      <field.SelectField
                        label='Classificação do imóvel'
                        placeholder={
                          hasPropertyClassificationsError
                            ? 'Erro ao carregar'
                            : 'Selecione'
                        }
                        required
                        disabled={
                          isViewMode ||
                          isLoadingCep ||
                          isPending ||
                          isLoadingPropertyClassifications ||
                          hasPropertyClassificationsError ||
                          (propertyClassifications?.length ?? 0) === 0
                        }
                        options={
                          propertyClassifications?.map(item => ({
                            value: String(item.code ?? ''),
                            label: item.description,
                          })) ?? []
                        }
                      />
                    )}
                  />
                </div>

                {hasPropertyClassificationsError && (
                  <div className='rounded-lg border border-alba-error-red-200 bg-alba-error-red-50 p-3 text-sm text-alba-error-red-700'>
                    <p className='font-medium'>
                      Não foi possível carregar as classificações do imóvel.
                    </p>
                    <p className='mt-1 text-xs text-alba-error-red-600'>
                      {propertyClassificationsError?.message ??
                        'Erro inesperado. Tente novamente.'}
                    </p>
                    <button
                      type='button'
                      onClick={() => refetchPropertyClassifications()}
                      className='mt-2 text-xs font-semibold text-alba-purple-500 hover:text-alba-purple-600'
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                <div className='space-y-2'>
                  {hasInsuredObjectTypesError && (
                    <div className='rounded-lg border border-alba-error-red-200 bg-alba-error-red-50 p-3 text-sm text-alba-error-red-700'>
                      <p className='font-medium'>
                        Não foi possível carregar os objetos segurados.
                      </p>
                      <p className='mt-1 text-xs text-alba-error-red-600'>
                        {insuredObjectTypesError?.message ??
                          'Erro inesperado. Tente novamente.'}
                      </p>
                      <button
                        type='button'
                        onClick={() => refetchInsuredObjectTypes()}
                        className='mt-2 text-xs font-semibold text-alba-purple-500 hover:text-alba-purple-600'
                      >
                        Tentar novamente
                      </button>
                    </div>
                  )}
                </div>

                <div className='space-y-3 sm:space-y-4'>
                  <RequiredFieldTooltip active>
                    <div className='space-y-2'>
                      <label className='text-sm font-bold text-alba-black-500'>
                        Objeto Segurado
                        <span className='text-alba-error-red-500'>*</span>
                      </label>
                    <RadioCard.Root
                      value={selectedInsuredObjectType}
                      onValueChange={details => {
                        const nextValue = String(details.value || '').trim();
                        setSelectedInsuredObjectType(nextValue);
                        setShowInsuredObjectError(false);
                        setInsuredObjectsErrors({});
                        form.setFieldValue('insuredObjectType', nextValue);

                        setInsuredObjects(prev => {
                          if (isCombinedInsuredObject(nextValue)) {
                            return prev.filter(
                              row =>
                                isCombinedInsuredObject(
                                  String(row.insuredObjectType || '').trim()
                                ) && String(row.riskValue || '').trim()
                            );
                          }

                          return orderInsuredObjects(
                            prev.filter(row =>
                              isIndividualInsuredObject(
                                String(row.insuredObjectType || '').trim()
                              )
                            )
                          );
                        });
                      }}
                      disabled={
                        isViewMode ||
                        isLoadingInsuredObjectTypes ||
                        hasInsuredObjectTypesError ||
                        isPending
                      }
                    >
                      <div className='grid grid-cols-2 gap-3 sm:flex sm:flex-wrap'>
                        {insuredObjectCardOptions.map(option => {
                          const Icon = option.icon;

                          return (
                            <RadioCard.Item
                              key={option.value}
                              value={option.value}
                              className='h-[108px] min-w-0 w-full rounded-xl sm:h-[120px] sm:w-52'
                            >
                              <RadioCard.ItemControl className='relative flex h-full flex-col items-start justify-center px-3 sm:px-4'>
                                <div className='absolute top-3 right-3'>
                                  <RadioCard.RadioButton />
                                </div>
                                <div className='flex flex-col items-start justify-center gap-2'>
                                  <Icon
                                    className='h-7 w-7 sm:h-8 sm:w-8'
                                    strokeWidth={1.6}
                                  />
                                  <RadioCard.ItemText className='text-left text-sm leading-tight whitespace-normal'>
                                    {option.displayLabel}
                                  </RadioCard.ItemText>
                                </div>
                              </RadioCard.ItemControl>
                              <RadioCard.ItemHiddenInput />
                            </RadioCard.Item>
                          );
                        })}
                      </div>
                    </RadioCard.Root>
                    {showInsuredObjectError && (
                      <Field.Root invalid>
                        <Field.ErrorText>
                          Selecione o objeto segurado
                        </Field.ErrorText>
                      </Field.Root>
                    )}
                    </div>
                  </RequiredFieldTooltip>

                  {selectedInsuredObjectType && (
                    <RequiredFieldTooltip active>
                    <Field.Root
                      invalid={
                        !!insuredObjectsErrors[selectedInsuredObjectType]
                      }
                      required
                      disabled={isViewMode}
                    >
                      <Field.Label>
                        Risco declarado
                        <span className='text-alba-error-red-500'>*</span>
                      </Field.Label>
                      <Field.Input
                        value={selectedRiskValue}
                        onChange={e => {
                          const value = formatCurrencyFromString(
                            e.target.value
                          );
                          setInsuredObjectsErrors(prev => {
                            if (!prev[selectedInsuredObjectType]) return prev;
                            const next = { ...prev };
                            delete next[selectedInsuredObjectType];
                            return next;
                          });
                          setInsuredObjects(prev => {
                            const next = prev.filter(
                              row =>
                                row.insuredObjectType !==
                                selectedInsuredObjectType
                            );

                            if (String(value || '').trim()) {
                              next.push({
                                insuredObjectType: selectedInsuredObjectType,
                                riskValue: value,
                              });
                            }

                            return orderInsuredObjects(next);
                          });
                        }}
                        placeholder='R$ 0,00'
                        disabled={isViewMode || isPending}
                      />
                      {insuredObjectsErrors[selectedInsuredObjectType] && (
                        <Field.ErrorText>
                          {insuredObjectsErrors[selectedInsuredObjectType]}
                        </Field.ErrorText>
                      )}
                    </Field.Root>
                    </RequiredFieldTooltip>
                  )}
                </div>
              </div>
            </div>

            <div className='flex flex-row items-center justify-between gap-3 self-stretch px-4 sm:gap-6 sm:px-6'>
              <Button
                variant='ghost'
                size='md'
                className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
                onClick={onBack}
              >
                <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
                Voltar
              </Button>
              <Button
                variant='primary'
                size='md'
                className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    Processando...
                    <Spinner size='sm' colorScheme='gray' />
                  </>
                ) : (
                  <>
                    Próximo
                    <ArrowRight className='h-5 w-5' strokeWidth={1.9} />
                  </>
                )}
              </Button>
            </div>
          </div>

          <PropertyDeniedDialog
            isOpen={
              isError && error instanceof ValidationError && !!error.detail
            }
            detail={error instanceof ValidationError ? error.detail : undefined}
            errors={error instanceof ValidationError ? error.messages : []}
          />
          <PropertyDeniedDialog
            isOpen={cepDenied.open}
            detail={cepDenied.detail}
            errors={cepDenied.errors}
          />
        </>
      );
    } catch {
      return <div>Erro ao carregar parâmetros da cotação</div>;
    }
  },
});
